import { useEffect } from 'react';
import compact from 'lodash/compact';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import * as K from 'common/constants';
import { QueryType } from 'common/graphqlTypes';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { getLrqErrorMessage, getRequestIds } from 'common/util/lrqRequest';
import { LRQRequest } from 'hooks/useLRQ/useLRQ.types';
import {
  graphResultsStatus,
  plotDataRequestsState,
  searchGraphPageStatusSelector,
  useProgressiveStateManagement,
} from 'components/search/SearchLogsPage/SearchStateManager.state';
import SearchCanceledPlaceholder from 'components/common/SearchCanceledPlaceholder/SearchCanceledPlaceholder';
import { HintWithLink } from 'components/common/SearchCanceledPlaceholder/SearchCanceledHints';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import GraphResults from './GraphResults';

type GraphResultsTimelineContainerProps = {
  noTimelineLegend?: boolean;
};

/**
 * GraphResultsContainer component. Primary responsibility is to grab the LRQ request results from the eventsCache,
 * and pass it along to the GraphResults Component.
 *
 * @prop noTimelineLegend    if true, do not show a legend above the graph
 */
function GraphResultsContainer({
  noTimelineLegend = false,
}: GraphResultsTimelineContainerProps): JSX.Element {

  const setAllSearchGraphPageStatuses = useSetRecoilState(searchGraphPageStatusSelector);
  const plotDataRequests = useRecoilValue(plotDataRequestsState);
  const eventCacheIds = getRequestIds(plotDataRequests, [ QueryType.PLOT, QueryType.BREAKDOWN ]);
  let eventCache = useEventCache(eventCacheIds);

  let isLoading = true;
  if (eventCache.results && eventCache.results.length > 0) {
    isLoading = !eventCache.results.every((result) => result.isDone);
  }

  // Handle any graphQL errors, render PageAlert
  // TODO: useEventCache hook may return [undefined] if no errors, which forces us to do the 'compact'
  // step. the hook should really just return empty array if no errors are present.
  const errorList = compact(eventCache.errors);
  let errorMessage = '';
  if (errorList.length > 0) {
    errorMessage = getLrqErrorMessage(errorList);
    isLoading = false;
  }
  const returnPreviousCache = eventCache.results?.[0]?.data == null || false;

  eventCache = useProgressiveStateManagement(eventCache, eventCacheIds, graphResultsStatus, returnPreviousCache);
  const { results } = eventCache;
  const isPreviousDataAvailable = results?.length > 0 && results.every((result) => result.isDone); // to know when to show skeleton UI
  const isRequestCanceled = plotDataRequests.some((request: LRQRequest) => request?.canceled);

  /**
   * Handle component statuses
   */
  useEffect(() => {
    if (isRequestCanceled) {
      // @ts-expect-error
      setAllSearchGraphPageStatuses(K.COMPONENT_STATUS.RESOLVED);
    }

    if (errorMessage) {
      // @ts-expect-error
      setAllSearchGraphPageStatuses(K.COMPONENT_STATUS.REJECTED);
    }

  }, [isRequestCanceled, errorMessage, setAllSearchGraphPageStatuses]);

  /**
   * Handle request cancellation
   */
  if (isRequestCanceled) {
    return (
      <SearchCanceledPlaceholder
        containerClassName={['SearchCanceledPlaceholder__Container--WithPadding']}
        message="Search Canceled"
        hint={<HintWithLink href={K.ROUTE.GRAPH} />}
      />
    );
  }

  /**
   * Handle error in the request
   */
  if (errorMessage) {
    return <ErrorPlaceholder error={{ message: errorMessage, title: 'Invalid Search' }} />;
  }

  return (
    <GraphResults isDimmed={returnPreviousCache} isLoading={isLoading} isPreviousDataAvailable={isPreviousDataAvailable} results={results} noTimelineLegend={noTimelineLegend} />
  );
}

export default GraphResultsContainer;
